/**
 * Capability Row
 *
 * One capability inside an `AreaRow` (or the Unassigned bucket) — drag
 * handle, coverage-status dot, inline-editable title, and an importance
 * badge. Clicking the row (anywhere but an interactive control) opens
 * `CapabilityDrawer`; double-clicking the title edits it in place without
 * navigating.
 *
 * @module features/product-map/components/CapabilityRow
 */

import { useState, type SyntheticEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical, Trash, Warning } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  chartThemes,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  EditableBadge,
  Flex,
  InlineEditableText,
  Text,
  Tooltip,
  TooltipTrigger,
  useTheme,
} from '@chatsdet/ui';
import type { ProductMapImportance } from '@chatsdet/types';
import { IMPORTANCE_OPTIONS, IMPORTANCE_VARIANT } from '../importance';
import { STAGE_LABEL, STAGE_VARIANT } from '../stage';
import {
  areaContainerId,
  COVERAGE_STATUS_DETAIL,
  COVERAGE_STATUS_LABEL,
  coverageStatusColor,
} from '../productMapLogic';
import { useDeprecateCapability, useUpdateCapability } from '../useProductMap';
import type { ProductCapability } from '../types';

function CoverageDot({ status }: { status: ProductCapability['coverageStatus'] }) {
  const { config } = useTheme();
  // Shares the coverage bar's palette (`chartThemes`) and status→color
  // mapping (`coverageStatusColor`), not Tailwind color classes — the
  // product ask was "the icons on the right should match the bar colors", so
  // the dot and the bar must read from one source of truth.
  const palette = chartThemes[config.theme];
  const color = coverageStatusColor(palette, status);
  const label = COVERAGE_STATUS_LABEL[status];
  const detail = COVERAGE_STATUS_DETAIL[status];
  const fullLabel = detail ? `${label} — ${detail}` : label;
  return (
    // `mr-1` clears the edit-mode field's left bleed (see the bleed note in
    // InlineEditableText.tsx) — applied here rather than on the title
    // wrapper so it's identical in both display and edit mode.
    <TooltipTrigger delay={150}>
      <span
        role="img"
        aria-label={fullLabel}
        className="mr-1 inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <Tooltip variant="light">{fullLabel}</Tooltip>
    </TooltipTrigger>
  );
}

type Props = {
  capability: ProductCapability;
  onOpen: () => void;
  /** Rendered inert inside a `DragOverlay` — no sortable wiring, no interactions. */
  isOverlay?: boolean;
  /** Opens the title already in edit mode — a row just created via "＋ capability". */
  autoEditTitle?: boolean;
};

export function CapabilityRow({ capability, onOpen, isOverlay = false, autoEditTitle = false }: Props) {
  const updateCapability = useUpdateCapability();
  const deprecateCapability = useDeprecateCapability(capability.propertyId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const sortable = useSortable({
    id: capability.id,
    // `containerId` lets the collision function group capability droppables
    // by owning container without re-deriving it from the rendered tree.
    // `capability.areaId` is re-parented by the mid-drag splice in
    // `useProductMapDragDrop`, so this stays correct against the row's
    // CURRENT rendered position mid-drag, not just its server-persisted one.
    data: { type: 'capability', label: capability.title, containerId: areaContainerId(capability.areaId) },
    disabled: isOverlay,
  });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const style = isOverlay
    ? undefined
    : {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      };

  const stop = (e: SyntheticEvent) => e.stopPropagation();

  return (
    <div
      ref={isOverlay ? undefined : setNodeRef}
      style={style}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'group/row flex items-center gap-2.5 px-3 py-1.5 text-sm transition-colors',
        'hover:bg-muted/50 cursor-pointer',
        isOverlay && 'rounded-md border border-border bg-card shadow-lg'
      )}
    >
      {!isOverlay && (
        <button
          type="button"
          className="shrink-0 -ml-1 flex items-center px-1 text-muted-foreground/50 hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
          aria-label={`Drag capability: ${capability.title}`}
          onClick={stop}
          {...attributes}
          {...listeners}
        >
          <DotsSixVertical size={14} weight="bold" />
        </button>
      )}

      <CoverageDot status={capability.coverageStatus} />
      {capability.blockedReason ? (
        <TooltipTrigger delay={150}>
          <Badge variant="destructive" className="shrink-0">Blocked</Badge>
          <Tooltip>Coverage blocked: {capability.blockedReason}</Tooltip>
        </TooltipTrigger>
      ) : null}
      {Array.isArray((capability as unknown as Record<string, unknown>).personas) && ((capability as unknown as Record<string, unknown>).personas as unknown[]).length > 0 ? (
        <span className="flex items-center gap-1 shrink-0">
          {((capability as unknown as Record<string, unknown>).personas as Array<Record<string, unknown>>).slice(0, 3).map((p) => (
            <Badge key={String(p.credentialTemplateId ?? p.id)} variant="default" className="text-xs">
              {String(p.name ?? p.credentialTemplateId).slice(0, 8)} {p.access === 'can' ? '✓' : p.access === 'cannot' ? '✗' : '◐'}
            </Badge>
          ))}
        </span>
      ) : null}

      {/* Shrink-to-content, NOT `flex-1`, while at rest — this wrapper stops
          click propagation so a single click on the title doesn't open the
          drawer mid-double-click, and shrink-wrapping keeps that dead zone
          from swallowing the rest of the row (the spacer below claims the
          remaining width instead). While editing there's no dead-zone risk
          (the input owns clicks and focus), so this wrapper takes `flex-1`
          too — but that alone isn't enough: `InlineEditableText`'s root is
          itself a flex item here, so it still sizes to its own content
          regardless of this wrapper's width. The inner conditional
          `flex-1` div is the one that actually grows; the edit-mode root it
          contains has no width class of its own, so it fills whatever
          width that inner div gives it. */}
      <div
        className={cn('flex min-w-0 items-baseline gap-1.5', editingTitle && 'flex-1')}
        onClick={stop}
        onKeyDown={stop}
        role="presentation"
      >
        {capability.code && (
          // `mr-2` clears the edit-mode field's left bleed (see the bleed
          // note in InlineEditableText.tsx), same as the dot's `mr-1` above.
          <Text size="xs" className="mr-2 shrink-0 font-mono text-muted-foreground/60">
            {capability.code}
          </Text>
        )}
        <div className={cn('min-w-0', editingTitle && 'flex-1')}>
          <InlineEditableText
            value={capability.title}
            onSave={(title) => updateCapability.mutate({ id: capability.id, title })}
            onEditingChange={setEditingTitle}
            ariaLabel={`Capability title: ${capability.title}`}
            placeholder="Untitled capability"
            className="min-w-0 truncate"
            autoEdit={autoEditTitle}
          />
        </div>
      </div>

      {!editingTitle && <div className="flex-1" aria-hidden />}

      <Badge size="sm" variant={STAGE_VARIANT[capability.status]} className="shrink-0">
        {STAGE_LABEL[capability.status]}
      </Badge>

      <div className="shrink-0" onClick={stop} onKeyDown={stop} role="presentation">
        <EditableBadge<ProductMapImportance>
          value={capability.importance}
          options={IMPORTANCE_OPTIONS}
          onChange={(importance) => updateCapability.mutate({ id: capability.id, importance })}
          variant={IMPORTANCE_VARIANT[capability.importance]}
          size="sm"
          aria-label={`Importance for ${capability.title}`}
        />
      </div>

      {!isOverlay && (
        <div className="shrink-0" onClick={stop} onKeyDown={stop} role="presentation">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            tooltip="Deprecate capability"
            aria-label={`Deprecate capability: ${capability.title}`}
            // Hidden until the row is hovered/focused, in keeping with the
            // dense list's low-chrome look — this is impactful and
            // infrequent, so it shouldn't compete with the title/badge at rest.
            className="text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100"
            onPress={() => setConfirmDelete(true)}
          >
            <Trash size={14} />
          </Button>

          <DialogTrigger isOpen={confirmDelete} onOpenChange={setConfirmDelete} size="md">
            <span />
            <Dialog title={`Deprecate "${capability.title}"?`}>
              <DialogContent>
                <Flex align="start" gap="md" className="py-2">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                    <Warning size={22} weight="fill" className="text-destructive" />
                  </span>
                  <Text size="sm" className="leading-relaxed text-muted-foreground">
                    This removes it from the active product map, along with its links to any
                    covering tests. It keeps its code, so a ticket that cites it still resolves —
                    you just won&rsquo;t see it here anymore. A capability with live rules can only be
                    deprecated after its rules are deprecated.
                  </Text>
                </Flex>
              </DialogContent>
              <DialogFooter className="mt-6">
                <Button variant="secondary" onPress={() => setConfirmDelete(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onPress={() => {
                    deprecateCapability.mutate(capability.id);
                    setConfirmDelete(false);
                  }}
                >
                  Deprecate capability
                </Button>
              </DialogFooter>
            </Dialog>
          </DialogTrigger>
        </div>
      )}
    </div>
  );
}
